/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { useMemo, useState } from 'react'
import { Box, Divider, Stack, Switch, Typography } from '@mui/material'
import { useForm, FormProvider, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useAppDispatch } from '../../../../store'
import FormInput from '../../../../components/FormElements/FormInput'
import GenericDateTimePicker from '../../../../components/GenericDateTimePicker'
import DefaultModalActions from '../../../../components/GenericModal/DefaultModalActions'
import { getTermsSectionsApi } from '../../../../store/slices/termsSectionsSlice/api'
import { getTerm } from '../../../../store/slices/termsSlice/selectors'
import { blue, grey } from '../../../../colors'
import ROUTES from '../../../../consts/paths'
import { type IUser } from '../../../../models/User'
import { type IAttribute, type ISection } from '../../../../models/TermsSection'
import _, { debounce, isEmpty } from 'lodash'
import { Link } from 'react-router-dom'
import api from '../../../../utils/api'
import { ApiAutoComplete } from '../../../../components/FormElements/ApiAutoComplete'
import AttributesMultiSelect from '../../../../components/FormElements/AttributesMultiSelect'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import Tab from '@mui/material/Tab'
import { CrossListCodeSectionDisplay } from './components/CrossListCodeSectionsDisplay'
import { getDirtyValues } from '../../../../utils/onlyUpdatedFields'
import MultiSelectApiAutoComplete from '../../../../components/FormElements/MultiSelectApiAutoComplete'
import { notification } from '../../../../components/Notifications'
import ValidationAlert from '../../../../components/ValidationAlert'
import { type ApiError } from '../../../../models/Common'

interface FormType {
  courseId: string
  abbreviation: string
  title: string
  isPrimary: boolean
  crossListCode?: string | null
  startTimestamp: Date
  endTimestamp: Date
  instructors?: IUser[]
  attributes?: IAttribute[]
}

interface CreateAndEditTermsSectionProps {
  selectedRow?: ISection | Record<string, any>
  handleCloseModal?: () => void
  params: Record<string, any>
}

export const CreateAndEditTermsSection: React.FC<CreateAndEditTermsSectionProps> = ({
  selectedRow,
  handleCloseModal = () => { },
  params
}) => {
  const [errors, setErrors] = useState<string>()
  const [tab, setTab] = useState<string>('1')
  const [loading, setLoading] = useState<boolean>(false)

  const dispatch = useAppDispatch()
  const termsDetails = getTerm()

  const isEditAction = Boolean(selectedRow)
  const { isPrimary, abbreviation, title, course, startTimestamp, endTimestamp, instructors, attributes } = selectedRow ?? {}
  const [crossListCode, setCrossListCode] = useState<string>(selectedRow?.crossListCode)
  const { abbreviation: courseAbbreviation } = course ?? {}

  const termFormSchema = yup.object().shape({
    courseId: yup.string().required('Course Id is required'),
    abbreviation: yup.string().required('Section Id is required'),
    title: yup.string().required('Title is required'),
    crossListCode: yup.string().nullable(),
    isPrimary: yup.boolean().required(),
    startTimestamp: yup.date()
      .required('Start Date is required')
      .max(yup.ref('endTimestamp'), 'Start Date must be before End Date'),
    endTimestamp: yup.date()
      .required('End Date is required')
      .min(yup.ref('startTimestamp'), 'End Date must be after Start Date')
      .test('is-before-term-end', 'End Date must be before term end date', function (value) {
        const { endTimestamp } = termsDetails ?? {}
        return !endTimestamp || value <= new Date(endTimestamp)
      })
  })

  const selectedInstructors = instructors?.map((user: { id: string, title: string }) => ({
    userId: user.id,
    name: user.title
  }))

  const methods = useForm<FormType>({
    resolver: yupResolver(termFormSchema),
    defaultValues: {
      courseId: courseAbbreviation,
      abbreviation,
      title,
      startTimestamp: startTimestamp ?? termsDetails?.startTimestamp,
      endTimestamp: endTimestamp ?? termsDetails?.endTimestamp,
      instructors: selectedInstructors,
      attributes,
      isPrimary: isPrimary ?? false,
      crossListCode: selectedRow?.crossListCode
    }
  })

  const { handleSubmit, control, formState: { dirtyFields } } = methods

  const onSubmit = async (data: FormType): Promise<void> => {
    if (isEmpty(data?.crossListCode)) {
      data.crossListCode = null
    }
    try {
      setLoading(true)
      const changedFields = getDirtyValues(dirtyFields, data)
      let res
      if (isEditAction) {
        res = await api.patch(`/api/sections/${selectedRow?.sectionId}`, changedFields)
        notification(`${res.data?.abbreviation} updated successfully to ${res.data?.term?.termName}`, { variant: 'success', subTitle: res.data?.abbreviation })
      } else {
        res = await api.post(`/api/sections/?term_id=${params?.termId}`, data)
        notification(`${res.data?.abbreviation} created successfully to ${res.data?.term?.termName}`, { variant: 'success', subTitle: res.data?.abbreviation })
      }

      handleCloseModal()
      await dispatch(getTermsSectionsApi({ params }))
    } catch (error) {
      const customError = error as ApiError
      if (customError.message) {
        setErrors(customError.message || 'Unknown error')
      }
    } finally {
      setLoading(false)
    }
  }

  const onInputChange = (value: string): void => {
    setCrossListCode(value)
  }

  const getDebouncedInput = useMemo(
    () => debounce(onInputChange, 500),
    [onInputChange]
  )

  return (
    <FormProvider {...methods}>
      <ValidationAlert errors={errors}/>
      <form onSubmit={handleSubmit(onSubmit)}>
        <TabContext value={tab}>

          <TabList onChange={(e, v: string) => { setTab(v) }}>
            <Tab label="Details" value="1" />
            <Tab label="Cross-Listing" value="2" />
          </TabList>

          <TabPanel value="1" sx={{ paddingTop: 4 }}>
            <Box display="flex" flexDirection="column" gap={2}>
              <ApiAutoComplete
                apiEndpoint="/api/divisions/summary/?category_name=Course"
                readOnly={isEditAction}
                required
                label="Course ID"
                labelField="abbreviation"
                name="courseId"
                valueField="abbreviation"
                selectedItem={selectedRow?.course} />

              <FormInput name="abbreviation" label="Section ID" control={control} required />

              {!isEditAction && (
                <Typography fontSize="14px" mt={1} color={grey[850]}>
                  Best practice: Spell out the whole Course Section ID
                </Typography>
              )}

              <FormInput name="title" label="Title" control={control} required />
              <MultiSelectApiAutoComplete
                name='instructors'
                apiEndpoint='/api/users/summary/'
                sortField='name'
                getOptionLabel={(option: IUser) => option.name}
                getOptionValue={(option: IUser) => option.userId}
                getOptionSubLabel={(option: IUser) => option.email}
                apiExtraParams={{ isFaculty: true }}
                label='Instructor(s)'
              />

              <Typography fontSize="14px" color={grey[850]}>
                If the instructor does not exist, create a user profile &nbsp;
                <Link to={ROUTES.FACULTY_AND_STAFF} target="_blank" style={{ color: blue[500] }}>
                  here.
                </Link>
              </Typography>
              <GenericDateTimePicker name="startTimestamp" label="Start Date" required />
              <GenericDateTimePicker name="endTimestamp" label="End Date" required />

              <Divider sx={{ mt: 4 }} />

              <Typography mt={4} fontWeight={600} >Optional Fields</Typography>
              <AttributesMultiSelect name="attributes" defaultAttributes={selectedRow?.attributes} />

            </Box>

          </TabPanel>
          <TabPanel value="2" sx={{ paddingTop: 4 }}>
            <Stack spacing={3}>
              <Box sx={{ pb: 2 }}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Controller
                    name="isPrimary"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Switch checked={value} onChange={onChange} />
                    )}
                  />
                  <Typography variant="p5" sx={{ fontWeight: 'bold' }}>Is Primary Course</Typography>
                </Box>
                <Typography variant="p5">Indicates that this Course is the primary sponsor of the course material</Typography>
              </Box>

              <FormInput name="crossListCode" label="Cross-List Code" control={control} onChange={(e) => { getDebouncedInput(e?.target?.value) }} />

              <CrossListCodeSectionDisplay crossListCode={crossListCode} termId={termsDetails?.termId} />
            </Stack>
          </TabPanel>
        </TabContext>

        <DefaultModalActions
          destructiveButtonProps={{
            onClick: handleCloseModal
          }}
          confirmButtonProps={{
            text: isEditAction ? 'Save' : 'Create',
            loading,
            type: 'submit'
          }}
        />
      </form>
    </FormProvider>
  )
}
